import React from 'react'
import PropTypes from 'prop-types'
import './ApprovalQueue.scss'

/**
 * ApprovalQueue - Pending approvals queue display
 *
 * @param {Array} approvals - Array of approval objects
 * @param {string} title - Widget title
 * @param {number} maxItems - Maximum items to display
 * @param {boolean} showViewAll - Show "View All" link
 * @param {Function} onViewAll - View all callback
 * @param {Function} onApprove - Approve callback
 * @param {Function} onReject - Reject callback
 * @param {Function} onItemClick - Item click callback
 * @param {boolean} loading - Show loading state
 */
const ApprovalQueue = ({
	approvals = [],
	title = 'Pending Approvals',
	maxItems = 5,
	showViewAll = false,
	onViewAll,
	onApprove,
	onReject,
	onItemClick,
	loading = false
}) => {
	const getTypeIcon = (type) => {
		const icons = {
			project: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z" />
				</svg>
			),
			checklist: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
				</svg>
			),
			task: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
				</svg>
			),
			document: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
				</svg>
			),
			default: (
				<svg viewBox="0 0 24 24" fill="currentColor">
					<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
				</svg>
			)
		}
		return icons[type] || icons.default
	}

	const getPriorityBadge = (priority) => {
		const config = {
			high: { label: 'High', variant: 'danger' },
			medium: { label: 'Medium', variant: 'warning' },
			low: { label: 'Low', variant: 'info' }
		}
		return config[priority] || null
	}

	const displayedApprovals = approvals.slice(0, maxItems)

	if (loading) {
		return (
			<div className="approval-queue">
				<div className="approval-queue__header">
					<h3 className="approval-queue__title">{title}</h3>
				</div>
				<div className="approval-queue__list">
					{[...Array(3)].map((_, i) => (
						<div key={i} className="approval-queue__item approval-queue__item--skeleton">
							<div className="skeleton skeleton--icon"></div>
							<div className="skeleton skeleton--content">
								<div className="skeleton skeleton--name"></div>
								<div className="skeleton skeleton--meta"></div>
							</div>
							<div className="skeleton skeleton--actions"></div>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="approval-queue">
			<div className="approval-queue__header">
				<h3 className="approval-queue__title">{title}</h3>
				{approvals.length > 0 && (
					<span className="approval-queue__count">{approvals.length}</span>
				)}
				{showViewAll && onViewAll && (
					<button className="approval-queue__view-all" onClick={onViewAll}>
						View All
					</button>
				)}
			</div>

			{displayedApprovals.length === 0 ? (
				<div className="approval-queue__empty">
					<svg viewBox="0 0 24 24" fill="currentColor" className="approval-queue__empty-icon">
						<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
					</svg>
					<p>No pending approvals</p>
				</div>
			) : (
				<div className="approval-queue__list">
					{displayedApprovals.map((item, index) => {
						const priorityBadge = getPriorityBadge(item.priority)

						return (
							<div
								key={item.id || index}
								className="approval-queue__item"
								onClick={() => onItemClick && onItemClick(item)}
								role={onItemClick ? 'button' : undefined}
								tabIndex={onItemClick ? 0 : undefined}
							>
								<div className="approval-queue__icon">
									{getTypeIcon(item.type)}
								</div>

								<div className="approval-queue__content">
									<p className="approval-queue__name">{item.title || item.name}</p>
									<div className="approval-queue__meta">
										{item.requester && (
											<span className="approval-queue__requester">
												By {item.requester}
											</span>
										)}
										{item.submitted_at && (
											<span className="approval-queue__time">{item.submitted_at}</span>
										)}
									</div>
								</div>

								{priorityBadge && (
									<span className={`approval-queue__priority approval-queue__priority--${priorityBadge.variant}`}>
										{priorityBadge.label}
									</span>
								)}

								{(onApprove || onReject) && (
									<div className="approval-queue__actions" onClick={(e) => e.stopPropagation()}>
										{onApprove && (
											<button
												className="approval-queue__btn approval-queue__btn--approve"
												onClick={() => onApprove(item)}
												title="Approve"
											>
												<svg viewBox="0 0 24 24" fill="currentColor">
													<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
												</svg>
											</button>
										)}
										{onReject && (
											<button
												className="approval-queue__btn approval-queue__btn--reject"
												onClick={() => onReject(item)}
												title="Reject"
											>
												<svg viewBox="0 0 24 24" fill="currentColor">
													<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
												</svg>
											</button>
										)}
									</div>
								)}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

ApprovalQueue.propTypes = {
	approvals: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			title: PropTypes.string,
			name: PropTypes.string,
			type: PropTypes.string,
			priority: PropTypes.oneOf(['high', 'medium', 'low']),
			requester: PropTypes.string,
			submitted_at: PropTypes.string
		})
	),
	title: PropTypes.string,
	maxItems: PropTypes.number,
	showViewAll: PropTypes.bool,
	onViewAll: PropTypes.func,
	onApprove: PropTypes.func,
	onReject: PropTypes.func,
	onItemClick: PropTypes.func,
	loading: PropTypes.bool
}

export default ApprovalQueue
